import { useCallback, useEffect, useRef, useState } from "react";
import Button from "@ingka/button";
import SSRIcon from "@ingka/ssr-icon";
import chevronDown from "@ingka/ssr-icon/paths/chevron-down-small";
import chevronUp from "@ingka/ssr-icon/paths/chevron-up-small";
import styles from "./HelpModal.module.css";

export type HelpPage = "top" | "analysis" | null;

interface HelpModalProps {
    page: HelpPage;
    onClose: () => void;
}

const INTRO_COLLAPSED_KEY = "mcc-help-intro-collapsed";

function getIntroCollapsed(): boolean {
    try {
        return localStorage.getItem(INTRO_COLLAPSED_KEY) === "1";
    } catch {
        return false;
    }
}

function setIntroCollapsed(collapsed: boolean): void {
    try {
        if (collapsed) {
            localStorage.setItem(INTRO_COLLAPSED_KEY, "1");
        } else {
            localStorage.removeItem(INTRO_COLLAPSED_KEY);
        }
    } catch {
        // Ignore storage errors
    }
}

function MccIntro() {
    const [collapsed, setCollapsed] = useState(getIntroCollapsed);

    const toggle = () => {
        setCollapsed((prev) => {
            const next = !prev;
            setIntroCollapsed(next);
            return next;
        });
    };

    return (
        <section className={styles.intro}>
            <button
                type="button"
                className={styles.introHeader}
                aria-expanded={!collapsed}
                onClick={toggle}
            >
                <SSRIcon paths={collapsed ? chevronDown : chevronUp} className={styles.introChevron} />
                <span>What is MCC?</span>
            </button>
            {!collapsed && (
                <div className={styles.introBody}>
                    <p>
                        <strong>MCC</strong> (Most Commercial Combinations) shows the product combinations customers
                        actually buy around a <em>product family</em>, such as PAX. A combination is at least one
                        frame plus optionally the complements bought with it: doors, interior, lighting, legs and so on. All data
                        is based on <strong>created</strong> sales including both store receipts and sales orders from
                        Common Sales. Use the product family picker in the header to switch family (for example PAX or
                        BESTÅ); the whole page is scoped to the one you choose.
                    </p>
                </div>
            )}
        </section>
    );
}

function TopHelp() {
    return (
        <>
            <MccIntro />
            <h3>What this page shows</h3>
            <p>
                A ranked list of the most-sold product family combinations. Each row lists the components of one
                combination and how frequently it sells, so you can see which complete solutions customers prefer.
            </p>
            <p>
                The percentage for the top row can look low. That is expected: each row is one specific combination,
                including exact item counts, and there are many possible combinations in the selected scope. A leading
                row at 1–2% can still be the most repeated pattern among a long tail of alternatives.
            </p>
            <h3>Scope selectors (top of page)</h3>
            <p>
                Apply Retail Unit, Sales Channels, Sub-market, Period and Frame Type filters to set what the list
                covers. These filters carry over between Top Combinations and Analysis
            </p>
            <h3>Component filters: With / Without</h3>
            <p>
                For each component (doors, interior, lighting, knobs &amp; handles) you pick which cases to
                include. Select <strong>With</strong> to show only combinations that have it, <strong>Without</strong>
                {" "}to show only those that lack it, or keep both selected to show all combinations regardless of that
                component. At least one option is always selected.
            </p>
            <h3>Ranking the list</h3>
            <p>
                Click the <strong>Receipts</strong> or <strong>Revenue (€)</strong> column header to choose how
                combinations are ranked. <strong>Receipts</strong> ranks by how many receipts matched, surfacing the
                most popular combinations. <strong>Revenue (€)</strong> ranks by total revenue (combination value
                multiplied by matching receipts, in EUR).
            </p>
        </>
    );
}

function AnalysisHelp() {
    return (
        <>
            <MccIntro />
            <h3>What this page shows</h3>
            <p>
                Instead of one ranked list, this page breaks the combinations down by component type, so you can see the
                mix of choices purchased by customers.
            </p>
            <h3>Scope selectors (top of page)</h3>
            <p>
                These dropdown filters at the top set the scope of what the page covers: Retail Unit, Sales Channels,
                Sub-market, and Period.
            </p>
            <h3>Summaries</h3>
            <p>
                The <strong>Selection Summary</strong> table lists sales revenue per retail unit, in both local currency
                and EUR. Add several retail units to compare them side by side and get a combined total in EUR.
            </p>
            <h3>Tabs</h3>
            <p>
                Each tab focuses on one component type (frames, doors, interior, lighting, knobs & handles, legs, top panels).
                Component-tab badges show the percentage of matching receipts whose combination includes that
                component. Inside a tab you see the distribution of colours, sizes, door solutions and other attributes.
            </p>
            <p>
                Product-type pies and tables group combinations by the distinct types present. Each combination
                contributes its receipt count once to its group; multiple types share a combined label. The separate
                exact-count lists distinguish patterns such as 1× versus 2× of the same item.
            </p>
            <h3>Deep-dive</h3>
            <p>
                Click a chart slice, legend value or table row to filter the matching combinations. For example,
                clicking &ldquo;grey-beige&rdquo; in the Frame colour chart applies a Frame colour filter across tabs.
                Command-click or long-press to include several values in one filter. A chart keeps its other slices
                visible when its own filter is selected, while filters from other charts narrow its distribution.
            </p>
            <p>
                When small slices are grouped into &ldquo;Other&rdquo;, uncheck <strong>Group small slices</strong> to see
                and select each value in the scrollable legend. &ldquo;Other&rdquo; itself is not selectable.
            </p>
            <p>
                Applied filters are shown as blue pill boxes and can be removed any time.
            </p>
        </>
    );
}

export function HelpModal({ page, onClose }: HelpModalProps) {
    const overlayRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [showFade, setShowFade] = useState(false);

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [onClose]);

    // Keep wheel/touch scroll inside the modal instead of the page behind it.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, []);

    // Keyboard accessibility: move focus into the dialog on open, trap Tab
    // within it, and restore focus to the trigger when it closes.
    useEffect(() => {
        const focusableSelector =
            'a[href], button:not([disabled]), textarea, input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const modal = modalRef.current;
        modal?.focus();

        const handleTab = (e: KeyboardEvent) => {
            if (e.key !== "Tab" || !modal) return;
            const items = Array.from(modal.querySelectorAll<HTMLElement>(focusableSelector));
            if (items.length === 0) {
                e.preventDefault();
                modal.focus();
                return;
            }
            const firstItem = items[0];
            const lastItem = items[items.length - 1];
            if (e.shiftKey && document.activeElement === firstItem) {
                e.preventDefault();
                lastItem.focus();
            } else if (!e.shiftKey && document.activeElement === lastItem) {
                e.preventDefault();
                firstItem.focus();
            }
        };
        document.addEventListener("keydown", handleTab);
        return () => {
            document.removeEventListener("keydown", handleTab);
            previouslyFocused?.focus?.();
        };
    }, []);

    const updateFade = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 1;
        setShowFade(el.scrollHeight > el.clientHeight + 1 && !atBottom);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        updateFade();
        const raf = requestAnimationFrame(updateFade);
        // Re-measure after late layout (e.g. page content loading behind the modal).
        const delayed = window.setTimeout(updateFade, 150);
        el.addEventListener("scroll", updateFade, { passive: true });
        window.addEventListener("resize", updateFade);
        const ro = new ResizeObserver(updateFade);
        ro.observe(el);
        if (contentRef.current) ro.observe(contentRef.current);
        ro.observe(document.documentElement);
        return () => {
            cancelAnimationFrame(raf);
            window.clearTimeout(delayed);
            el.removeEventListener("scroll", updateFade);
            window.removeEventListener("resize", updateFade);
            ro.disconnect();
        };
    }, [updateFade, page]);

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === overlayRef.current) onClose();
    };

    const title = page === "analysis" ? "About Analysis" : "About Top Combinations";

    return (
        <div className={styles.overlay} ref={overlayRef} onClick={handleOverlayClick}>
            <div className={styles.modal} ref={modalRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title}>
                <h2 className={styles.title}>{title}</h2>
                <div className={styles.body} ref={scrollRef}>
                    <div className={styles.content} ref={contentRef}>
                        {page === "analysis" ? <AnalysisHelp /> : <TopHelp />}
                    </div>
                    {showFade && <div className={styles.fade} aria-hidden="true" />}
                </div>
                <div className={styles.buttons}>
                    <Button text="Got it" type="emphasised" size="xsmall" onClick={onClose} />
                </div>
            </div>
        </div>
    );
}
